import React, { useEffect, useMemo, useState } from 'react'
import { apiService } from '../../services/api'
import {
  ReportShell, HeaderButton, FilterBar, KpiStrip, Icon, fmt, T,
  useDistinctFilters,
  buildOplFilterFields, buildPlkFilterFields, buildTmplFilterFields,
} from '../report-shell'
import AgentDrillDown from './AgentDrillDown'
import SendReportModal from './SendReportModal'

const monthNames = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
]

const accentByOp = {
  plus:    '#00a651',
  orange:  '#ff7900',
  tmobile: '#e20074',
}

/**
 * Generic 'Sprzedaż per Agent' page (Plus / Orange / T-Mobile).
 *
 * Props:
 *   operator    : 'plus' | 'orange' | 'tmobile'
 *   title       : page title
 *   description : sub-title text
 */
const AgentSalesView = ({ operator, title, description }) => {
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const [filters, setFilters] = useState({})
  const [productGroup, setProductGroup] = useState(null)
  const [productGroupOptions, setProductGroupOptions] = useState([])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [sendModalScope, setSendModalScope] = useState('agent')
  const [sendModalValue, setSendModalValue] = useState(null)

  const operatorCode = operator === 'plus' ? 'PLUS'
                      : operator === 'orange' ? 'ORANGE'
                      : operator === 'tmobile' ? 'TMOBILE' : null

  const opOpaPrefix = operator === 'plus' ? 'plk' : operator === 'orange' ? 'opl' : 'tmpl'
  const accentColor = accentByOp[operator]

  const distinct = useDistinctFilters(opOpaPrefix)

  // Product groups (operator-scoped).
  useEffect(() => {
    let cancelled = false
    if (!operatorCode) { setProductGroupOptions([]); return }
    apiService.getProductGroups(operatorCode)
      .then(r => {
        if (cancelled) return
        const explicit = (r.data?.groups || []).filter(g => !g.is_default && !g.is_aggregate)
        setProductGroupOptions(explicit)
      })
      .catch(() => { if (!cancelled) setProductGroupOptions([]) })
    return () => { cancelled = true }
  }, [operatorCode])

  // Compose API filters object.
  const apiFilters = (kind) => {
    if (operator === 'tmobile') {
      const from = new Date(year, month - 1, 1).toISOString().slice(0, 10)
      const to = new Date(year, month, 1).toISOString().slice(0, 10)
      return { from, to }
    }
    const f = { month, year, ...filters }
    if (productGroup) f.product_group = productGroup
    return f
  }

  const fetchData = async () => {
    setLoading(true); setError(null)
    try {
      const fetcher =
        operator === 'plus'    ? apiService.getPlusAgents :
        operator === 'orange'  ? apiService.getOrangeAgents :
        operator === 'tmobile' ? apiService.getTmobileAgents :
        null
      if (!fetcher) throw new Error(`Unknown operator: ${operator}`)
      const resp = await fetcher(apiFilters('list'))
      setData(resp.data)
      setLastUpdated(new Date())
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setLoading(false) }
  }

  const exportExcel = async () => {
    setExporting(true); setError(null)
    try {
      const exporter =
        operator === 'plus'    ? apiService.exportPlusAgents :
        operator === 'orange'  ? apiService.exportOrangeAgents :
        operator === 'tmobile' ? apiService.exportTmobileAgents :
        null
      if (!exporter) throw new Error(`Unknown operator: ${operator}`)
      const resp = await exporter(apiFilters('export'))
      const url = window.URL.createObjectURL(new Blob([resp.data]))
      const link = document.createElement('a')
      link.href = url
      const period = operator === 'tmobile'
        ? `${apiFilters('export').from}_do_${apiFilters('export').to}`
        : `${year}-${String(month).padStart(2, '0')}`
      link.setAttribute('download', `Sprzedaz_per_Agent_${operator.toUpperCase()}_${period}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setExporting(false) }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [operator, month, year, productGroup, JSON.stringify(filters)])

  // ─ Filter / period config ────────────────────────────────────
  const years = useMemo(() => {
    const cy = new Date().getFullYear()
    return [cy, cy - 1, cy - 2]
  }, [])

  const periods = useMemo(() => ([
    {
      type: 'select', key: 'month', label: 'Miesiąc',
      value: month, display: monthNames[month - 1],
      options: monthNames.map((n, i) => ({ value: i + 1, label: n })),
      onChange: (v) => setMonth(Number(v)),
      allowClear: false,
    },
    {
      type: 'select', key: 'year', label: 'Rok',
      value: year, display: String(year),
      options: years.map(y => ({ value: y, label: String(y) })),
      onChange: (v) => setYear(Number(v)),
      allowClear: false,
    },
  ]), [month, year, years])

  // Operator-specific filter chips (from /admin/pos/distinct).
  const distinctFilterFields = useMemo(() => {
    if (operator === 'plus')   return buildPlkFilterFields(distinct, filters, setFilters)
    if (operator === 'orange') return buildOplFilterFields(distinct, filters, setFilters)
    if (operator === 'tmobile')return buildTmplFilterFields(distinct, filters, setFilters)
    return []
  }, [operator, distinct, filters])

  // Product group goes first (when explicit groups exist > 1).
  const filterFields = useMemo(() => {
    const fs = []
    if (productGroupOptions.length > 1) {
      fs.push({
        type: 'select', key: 'product_group', label: 'Grupa produktów',
        value: productGroup,
        display: productGroup
          ? (productGroupOptions.find(g => g.code === productGroup)?.name ?? productGroup)
          : null,
        options: [
          { value: null, label: 'Wszystkie' },
          ...productGroupOptions.map(g => ({ value: g.code, label: g.name })),
        ],
        onChange: (v) => setProductGroup(v || null),
      })
    }
    return [...fs, ...distinctFilterFields]
  }, [productGroup, productGroupOptions, distinctFilterFields])

  const onResetFilters = () => {
    setFilters({})
    setProductGroup(null)
  }

  // ─ KPI strip ─────────────────────────────────────────────────
  const kpiTiles = useMemo(() => {
    if (!data) return []
    const t = data.totals || {}
    const agentCount = t.agent_count ?? data.agents?.length ?? 0
    const salonCount = t.salon_count ?? 0
    const sales = t.sales_in_period ?? 0
    const avgPerSalon = salonCount > 0 ? (sales / salonCount) : 0
    return [
      { key: 'agents', label: 'Agentów', value: fmt.int(agentCount), sub: 'firmy agencyjne' },
      { key: 'salons', label: 'Salonów', value: fmt.int(salonCount), sub: 'punktów sprzedaży' },
      {
        key: 'sales', label: 'Sprzedaż okres',
        value: fmt.int(sales), unit: 'szt.',
        sub: `${monthNames[month - 1]} ${year}`,
      },
      {
        key: 'avg', label: 'Średnia / salon',
        value: avgPerSalon.toFixed(2).replace('.', ','),
        unit: 'szt./salon',
        sub: salonCount > 0 ? 'sprzedaż / liczba salonów' : '—',
      },
    ]
  }, [data, month, year])

  // ─ Drill-down filter resolver (per agent) ─────────────────────
  const drillFilterFor = (a) => {
    if (operator === 'plus') {
      return {
        agent: a.agent || undefined,
        rks: filters.rks || undefined,
        pos_type: filters.pos_type || undefined,
        region: filters.region || undefined,
        kr: filters.kr || undefined,
      }
    }
    if (operator === 'orange') {
      return {
        agent: a.agent && a.agent !== '(brak)' ? a.agent : undefined,
        rksa: filters.rksa || undefined,
        pos_type: filters.pos_type || undefined,
        region: filters.region || undefined,
        dystrykt: filters.dystrykt || undefined,
        xdm: filters.xdm || undefined,
        product_group: productGroup || undefined,
      }
    }
    const f = apiFilters('list')
    return {
      agentCode: a.agent_code || undefined,
      agentName: a.agent_code ? undefined : a.agent_name,
      from: f.from, to: f.to,
    }
  }

  const timestamp = lastUpdated
    ? `Aktualizacja: ${lastUpdated.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })} · ${lastUpdated.toLocaleDateString('pl-PL')}`
    : 'dane jeszcze nie wczytane'

  return (
    <>
      <ReportShell
        title={title}
        subtitle={description}
        breadcrumb={['KAM-SFA', operator === 'plus' ? 'Plus' : operator === 'orange' ? 'Orange' : 'T-Mobile', 'Sprzedaż per Agent']}
        accentColor={accentColor}
        actions={
          <>
            <HeaderButton onClick={fetchData} disabled={loading}>
              <Icon.refresh /> {loading ? 'Ładowanie…' : 'Odśwież'}
            </HeaderButton>
            <HeaderButton primary onClick={exportExcel} disabled={exporting || !data}>
              <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
            </HeaderButton>
          </>
        }
        filterBar={
          <FilterBar
            periods={periods}
            periodRight={timestamp}
            filters={filterFields}
            onReset={onResetFilters}
          />
        }
        kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
      >
        {error && (
          <div style={{
            padding: '12px 16px', borderRadius: 10,
            background: '#fee2e2', color: '#dc2626',
            border: '1px solid #fecaca',
            fontSize: 13, fontFamily: T.fontUI,
          }}>{error}</div>
        )}

        {loading && !data && (
          <div style={{ padding: 32, textAlign: 'center', color: '#8a827a', fontSize: 13 }}>
            Ładowanie danych…
          </div>
        )}

        {data && (data.agents || []).length === 0 && (
          <div style={{
            padding: '28px 18px', textAlign: 'center',
            color: '#57534e', fontSize: 13,
            background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10,
          }}>Brak agentów dla wybranych filtrów.</div>
        )}

        {data && (data.agents || []).map((a, i) => {
          const agentLabel = a.agent || a.agent_name || a.agent_code || `Agent #${i + 1}`
          return (
            <AgentDrillDown
              key={(a.agent_code || a.agent || a.agent_name || '') + '|' + i}
              agent={agentLabel}
              operator={operator}
              accentColor={accentColor}
              summary={{
                rks: a.rks || a.rksa,
                salon_count: a.salon_count,
                sales: a.sales_in_period,
                pos_type: a.pos_type || a.siec_sprzedazy || a.ownership_type,
              }}
              filters={drillFilterFor(a)}
              onSendReport={(label) => {
                const key = operator === 'tmobile'
                  ? (a.agent_code || label)
                  : label
                setSendModalScope('agent')
                setSendModalValue(key)
                setSendModalOpen(true)
              }}
              onSendScope={(scope, value) => {
                setSendModalScope(scope)
                setSendModalValue(value)
                setSendModalOpen(true)
              }}
            />
          )
        })}
      </ReportShell>

      <SendReportModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        operator={operator}
        scope={sendModalScope}
        scopeValue={sendModalValue}
        periodStart={operator === 'tmobile' ? apiFilters('list').from : `${year}-${String(month).padStart(2, '0')}-01`}
        periodEnd={operator === 'tmobile' ? apiFilters('list').to : (() => {
          const d = new Date(year, month, 1)
          return d.toISOString().slice(0, 10)
        })()}
      />
    </>
  )
}

export default AgentSalesView
